import { useRef } from "react";
import { useAuth } from "react-oidc-context";
import {
  CognitoIdentityProviderClient,
  GlobalSignOutCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const REGION = "us-east-1"; // your Cognito user pool region
const CLIENT_ID = "rac08r0fkkbqi1f5umt50lkob";
const DOMAIN = "https://auth.kloudscan.com";

// Optional: change this if you want a different post-logout page
const POST_LOGOUT_PATH = "/logout";

const useLogout = () => {
  const auth = useAuth();
  const inFlightRef = useRef(false); // idempotency across renders

  const getLogoutUri = () => {
    if (typeof window === "undefined") return "/";
    const postLogout = `${window.location.origin}${POST_LOGOUT_PATH}`;
    // Cognito Hosted UI logout
    return (
      `${DOMAIN}/logout?client_id=${encodeURIComponent(CLIENT_ID)}` +
      `&logout_uri=${encodeURIComponent(postLogout)}`
    );
  };

  const broadcastLogout = () => {
    try {
      // Cross-tab signal and durable flag to gate other listeners
      localStorage.setItem("authState", "signOut");
      sessionStorage.setItem("logoutInProgress", "1");
      if ("BroadcastChannel" in window) {
        const channel = new BroadcastChannel("auth");
        channel.postMessage("logout");
        channel.close();
      }
    } catch {
      /* no-op */
    }
  };

  const globalSignOut = async (accessToken) => {
    if (!accessToken) return;
    try {
      const client = new CognitoIdentityProviderClient({ region: REGION });
      const command = new GlobalSignOutCommand({ AccessToken: accessToken });
      await client.send(command);
    } catch (err) {
      // Not fatal — Hosted UI logout will still clear IdP cookie
      console.warn("Global sign-out failed (continuing):", err);
    }
  };

  const clearLocalOidc = async () => {
    try {
      await auth.removeUser?.();
      await auth.clearStaleState?.();
    } catch {
      /* ignore */
    }
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("oidc.")) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  };

  const redirectToHostedUI = () => {
    if (typeof window === "undefined") return;
    const logoutUrl = getLogoutUri();
    try {
      window.location.assign(logoutUrl);
    } catch {
      window.location.href = logoutUrl;
    }
  };

  const handleLogout = async () => {
    if (inFlightRef.current) return; // idempotent
    inFlightRef.current = true;

    // 1) mark + broadcast (do this FIRST so other listeners back off)
    broadcastLogout();

    // 2) global sign-out (best-effort)
    const accessToken = auth?.user?.access_token;
    await globalSignOut(accessToken);

    // 3) clear local OIDC artifacts
    await clearLocalOidc();

    // 4) Hosted UI logout (clears IdP cookie; redirects to POST_LOGOUT_PATH)
    redirectToHostedUI();
  };

  return { handleLogout };
};

export default useLogout;
